import { CalendarOutlined, TeamOutlined } from "@ant-design/icons";
import { DatePicker } from "antd";
import dayjs from "dayjs";
import { AnimatePresence, motion } from "framer-motion";
import { useMemo, useState } from "react";

const { RangePicker } = DatePicker;

/* ════════════════════════════════════════════════════════════════
   DATA LAYER  (no UI here)
   ⚠ DUMMY DATA — replace `ALL_BILLS` / `useSalesmanCommissions` with the
   real API call. The UI only needs rows shaped like:
     { salesman, sales, rate, commission, bills }
   one per salesman for the selected date range.
   Dates are relative to today so the demo always shows data.
   ════════════════════════════════════════════════════════════════ */
const daysAgo = (n) => dayjs().subtract(n, "day").format("YYYY-MM-DD");

const ALL_BILLS = [
  { bill: "B-1041", rawDate: daysAgo(0), salesman: "Rakesh", amount: 148500, rate: 1.5 },
  { bill: "B-1040", rawDate: daysAgo(0), salesman: "Suresh", amount: 92000, rate: 1.0 },
  { bill: "B-1038", rawDate: daysAgo(1), salesman: "Rakesh", amount: 62000, rate: 1.5 },
  { bill: "B-1035", rawDate: daysAgo(2), salesman: "Anita", amount: 171200, rate: 2.0 },
  { bill: "B-1031", rawDate: daysAgo(3), salesman: "Suresh", amount: 45800, rate: 1.0 },
  { bill: "B-1028", rawDate: daysAgo(5), salesman: "Meena", amount: 93400, rate: 1.25 },
  { bill: "B-1024", rawDate: daysAgo(9), salesman: "Anita", amount: 81600, rate: 2.0 },
  { bill: "B-1019", rawDate: daysAgo(14), salesman: "Meena", amount: 58900, rate: 1.25 },
  { bill: "B-1015", rawDate: daysAgo(22), salesman: "Kiran", amount: 137200, rate: 1.0 },
  { bill: "B-1009", rawDate: daysAgo(40), salesman: "Rakesh", amount: 82100, rate: 1.5 },
];

const useSalesmanCommissions = (range) =>
  useMemo(() => {
    const from = range?.[0]?.startOf("day");
    const to = range?.[1]?.endOf("day");
    const bills =
      from && to
        ? ALL_BILLS.filter((b) => {
          const d = dayjs(b.rawDate);
          return !d.isBefore(from) && !d.isAfter(to);
        })
        : ALL_BILLS;

    const groups = bills.reduce((acc, b) => {
      const g = acc[b.salesman] || { sales: 0, commission: 0, bills: 0 };
      g.sales += b.amount;
      g.commission += (b.amount * b.rate) / 100;
      g.bills += 1;
      acc[b.salesman] = g;
      return acc;
    }, {});

    return Object.entries(groups)
      .map(([salesman, g]) => ({
        salesman,
        ...g,
        rate: g.sales > 0 ? (g.commission / g.sales) * 100 : 0, // effective %
      }))
      .sort((a, b) => b.commission - a.commission);
  }, [range]);

/* ════════════════════════════════════════════════════════════════
   UI
   ════════════════════════════════════════════════════════════════ */
const COLS = [
  { label: "Salesman", flex: 1 },
  { label: "Sales", w: 62, right: true },
  { label: "Rate", w: 42, right: true },
  { label: "Commission", w: 72, right: true },
];

const AVATAR_COLORS = ["#6c5ce7", "#00cec9", "#fd79a8", "#e0a526", "#00b894", "#e17055"];

const fmtShort = (n) =>
  n >= 100000
    ? `${(n / 100000).toFixed(1)}L`
    : n >= 1000
      ? `${(n / 1000).toFixed(1)}k`
      : `${Number(n).toFixed(0)}`;

/* ── Styled range picker that blends into the gradient header ── */
const HeaderRangePicker = ({ value, onChange }) => (
  <>
    <style>{`
      .comm-rp .ant-picker {
        background: rgba(255,255,255,0.14) !important;
        border: 1px solid rgba(255,255,255,0.30) !important;
        border-radius: 20px !important;
        padding: 2px 10px !important;
        height: 24px !important;
        box-shadow: none !important;
      }
      .comm-rp .ant-picker:hover,
      .comm-rp .ant-picker-focused {
        border-color: rgba(255,255,255,0.55) !important;
        box-shadow: none !important;
      }
      .comm-rp .ant-picker-input > input {
        color: #fff !important;
        font-size: 10px !important;
        font-weight: 700 !important;
        width: 56px !important;
      }
      .comm-rp .ant-picker-input > input::placeholder { color: rgba(255,255,255,0.55) !important; }
      .comm-rp .ant-picker-separator,
      .comm-rp .ant-picker-suffix { color: rgba(255,255,255,0.6) !important; font-size: 10px !important; }
      .comm-rp .ant-picker-clear { display: none !important; }
    `}</style>
    <div className="comm-rp">
      <RangePicker
        value={value}
        onChange={onChange}
        format="DD MMM YY"
        size="small"
        allowClear={false}
        suffixIcon={
          <CalendarOutlined
            style={{ color: "rgba(255,255,255,0.7)", fontSize: 10 }}
          />
        }
        popupStyle={{ zIndex: 9999 }}
        disabledDate={(c) => c && c.isAfter(dayjs(), "day")}
      />
    </div>
  </>
);

const SalesmanCommissions = () => {
  const [range, setRange] = useState([dayjs(), dayjs()]);
  const data = useSalesmanCommissions(range);

  const totalCommission = data.reduce((a, r) => a + r.commission, 0);
  const totalSales = data.reduce((a, r) => a + r.sales, 0);

  return (
    <div
      style={{
        background: "#fff",
        borderRadius: 12,
        boxShadow: "0 2px 14px rgba(44,27,105,0.10)",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        height: "100%",
      }}
    >
      {/* ── Gradient header — single row ── */}
      <div
        style={{
          padding: "7px 14px",
          background: "linear-gradient(90deg,#e17055 0%,#fd79a8 100%)",
          position: "relative",
          overflow: "hidden",
          minHeight: 38,
          display: "flex",
          alignItems: "center",
        }}
      >
        <div
          style={{
            position: "absolute",
            right: -20,
            top: -20,
            width: 70,
            height: 70,
            borderRadius: "50%",
            background: "rgba(255,255,255,0.07)",
            pointerEvents: "none",
          }}
        />
        <div
          style={{
            position: "absolute",
            left: -10,
            bottom: -15,
            width: 50,
            height: 50,
            borderRadius: "50%",
            background: "rgba(255,255,255,0.04)",
            pointerEvents: "none",
          }}
        />

        {/* Left: icon + title */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            flex: 1,
            minWidth: 0,
          }}
        >
          <div
            style={{
              width: 26,
              height: 26,
              borderRadius: 7,
              background: "rgba(255,255,255,0.18)",
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <TeamOutlined style={{ fontSize: 13, color: "#fff" }} />
          </div>
          <div style={{ minWidth: 0 }}>
            <div
              style={{
                fontSize: 12,
                fontWeight: 800,
                color: "#fff",
                lineHeight: 1.2,
                whiteSpace: "nowrap",
              }}
            >
              Salesman Commissions
            </div>
            <div
              style={{
                fontSize: 9,
                color: "rgba(255,255,255,0.75)",
                whiteSpace: "nowrap",
              }}
            >
              {data.length} salesmen · ₹{fmtShort(totalCommission)} payable
            </div>
          </div>
        </div>

        {/* Right: date range picker + pulse dot */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            flexShrink: 0,
          }}
        >
          <HeaderRangePicker value={range} onChange={(v) => v && setRange(v)} />
          <motion.div
            animate={{ scale: [1, 1.5, 1], opacity: [1, 0.4, 1] }}
            transition={{ repeat: Infinity, duration: 1.6, ease: "easeInOut" }}
            style={{
              width: 7,
              height: 7,
              borderRadius: "50%",
              background: "#90be6d",
              flexShrink: 0,
            }}
          />
        </div>
      </div>

      {/* ── Column headers ── */}
      <div
        style={{
          display: "flex",
          gap: 6,
          padding: "4px 14px",
          background: "rgba(108,92,231,0.13)",
          borderBottom: "1px solid rgba(108,92,231,0.15)",
        }}
      >
        {COLS.map((c) => (
          <div
            key={c.label}
            style={{
              width: c.w,
              flex: c.flex,
              fontSize: 9,
              fontWeight: 800,
              color: "#6c5ce7",
              textTransform: "uppercase",
              letterSpacing: 0.6,
              textAlign: c.right ? "right" : "left",
            }}
          >
            {c.label}
          </div>
        ))}
      </div>

      {/* ── Rows ── */}
      <div style={{ overflowY: "auto", flex: 1, overflowX: "hidden" }}>
        <AnimatePresence mode="wait">
          <motion.div
            key={JSON.stringify(range)}
            initial={{ opacity: 0, y: 5 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            {data.length === 0 ? (
              <div
                style={{
                  textAlign: "center",
                  padding: "20px 0",
                  color: "#b8a4e8",
                  fontSize: 11,
                  fontWeight: 600,
                }}
              >
                No commissions in this range
              </div>
            ) : (
              <>
                {data.map((r, i) => {
                  const color = AVATAR_COLORS[i % AVATAR_COLORS.length];
                  return (
                    <motion.div
                      key={r.salesman}
                      initial={{ opacity: 0, x: -8 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: i * 0.04, duration: 0.25 }}
                      whileHover={{
                        borderLeft: "3px solid #6c5ce7",
                        paddingLeft: 11,
                      }}
                      style={{
                        display: "flex",
                        gap: 6,
                        padding: "7px 14px",
                        borderBottom: "1px solid #f0eaff",
                        borderLeft: "3px solid transparent",
                        alignItems: "center",
                        cursor: "default",
                        background: i % 2 === 0 ? "#fff" : "#fafafa",
                        transition: "background 0.15s, border-left 0.15s",
                      }}
                    >
                      {/* Salesman */}
                      <div
                        style={{
                          flex: 1,
                          minWidth: 0,
                          display: "flex",
                          alignItems: "center",
                          gap: 7,
                        }}
                      >
                        <div
                          style={{
                            width: 22,
                            height: 22,
                            borderRadius: 7,
                            flexShrink: 0,
                            background: `${color}22`,
                            color,
                            fontSize: 10,
                            fontWeight: 800,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          {r.salesman.charAt(0).toUpperCase()}
                        </div>
                        <div style={{ minWidth: 0 }}>
                          <div
                            style={{
                              fontSize: 11,
                              fontWeight: 700,
                              color: "#2d1b69",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {r.salesman}
                          </div>
                          <div style={{ fontSize: 9, color: "#aaa" }}>
                            {r.bills} bill{r.bills !== 1 ? "s" : ""}
                          </div>
                        </div>
                      </div>

                      {/* Sales */}
                      <div
                        style={{
                          width: 62,
                          textAlign: "right",
                          fontSize: 10,
                          fontWeight: 600,
                          color: "#777",
                        }}
                      >
                        ₹{fmtShort(r.sales)}
                      </div>

                      {/* Rate */}
                      <div
                        style={{
                          width: 42,
                          textAlign: "right",
                          fontSize: 10,
                          fontWeight: 700,
                          color: "#6c5ce7",
                        }}
                      >
                        {r.rate.toFixed(2)}%
                      </div>

                      {/* Commission */}
                      <div style={{ width: 72, textAlign: "right" }}>
                        <span
                          style={{
                            fontSize: 9,
                            fontWeight: 700,
                            color: "#43aa8b",
                          }}
                        >
                          ₹
                        </span>
                        <span
                          style={{
                            fontSize: 12,
                            fontWeight: 900,
                            color: "#43aa8b",
                          }}
                        >
                          {fmtShort(r.commission)}
                        </span>
                      </div>
                    </motion.div>
                  );
                })}

                {/* Totals row */}
                <div
                  style={{
                    display: "flex",
                    gap: 6,
                    padding: "6px 14px",
                    background: "rgba(108,92,231,0.07)",
                    borderLeft: "3px solid transparent",
                    alignItems: "center",
                  }}
                >
                  <div
                    style={{
                      flex: 1,
                      fontSize: 9,
                      fontWeight: 800,
                      color: "#6c5ce7",
                      textTransform: "uppercase",
                      letterSpacing: 0.6,
                    }}
                  >
                    Total
                  </div>
                  <div
                    style={{
                      width: 62,
                      textAlign: "right",
                      fontSize: 10,
                      fontWeight: 800,
                      color: "#4c3494",
                    }}
                  >
                    ₹{fmtShort(totalSales)}
                  </div>
                  <div style={{ width: 42 }} />
                  <div
                    style={{
                      width: 72,
                      textAlign: "right",
                      fontSize: 12,
                      fontWeight: 900,
                      color: "#43aa8b",
                    }}
                  >
                    ₹{fmtShort(totalCommission)}
                  </div>
                </div>
              </>
            )}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
};

export default SalesmanCommissions;